import { ChangeDetectorRef, Component, Input, OnInit, OnChanges, OnDestroy, ViewChild, ViewEncapsulation, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { QnSbnService } from '../services/qn-sbn-service.service';
import { OneWebComponentsAngularModule } from '@one/angular';
import { AppUtils } from '../utlis/app-utils';
import { CommonModule } from '@angular/common';
import { SnackbarService } from '../services/snackbar.service';

export interface NotificationData {
  dateCreated: Date;
  country: string;
  type: string;
  localId: string;
  accountId: string;
  accountName: string;
  accountStatus: string;
  delivery: string;
  feedback: string;
  globalId?: string;
  dateSent?: Date;
  dateDelivered?: Date;
  dateRead?: Date;
  dateFeedback?: Date;
  deliveryFailures?: string;
  daysToOpen?: number;
  daysToSubmit?: number;
}

export interface ColumnDefinition {
  key: string;
  label: string;
  hidden: boolean;
}

@Component({
  selector: 'dl-dashboard-table',
  standalone: true,
  imports: [OneWebComponentsAngularModule, CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './dashboard-table.component.html',
  styleUrls: ['./dashboard-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DashboardTableComponent implements OnInit, OnChanges, OnDestroy {

  @Input() dbTableData: NotificationData[] = [];
  
  dataSource: NotificationData[] = [];
  filteredData: NotificationData[] = [];
  paginatedData: NotificationData[] = [];
  
  // Pagination properties
  currentPage = 0;
  pageSize = 10; // Default to first option in pageSizeOptions
  pageSizeOptions = [10, 25, 50, 100, 250, 500];
  
  // Sorting properties
  sortColumn = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  searchBox = new FormControl();
  searchValue = '';
  isLoading = false;
  totalCount = 0;
  selectedRow: NotificationData | null = null;
  tempColumnState: { [key: string]: boolean } = {};
  showColumnMenu = false;

  // Default columns that should always remain visible
  defaultColumns = ['dateCreated', 'country', 'type', 'localId', 'accountId', 'accountName', 'accountStatus', 'delivery', 'feedback'];

  // Column definitions based on the specifications
  allColumns: ColumnDefinition[] = [
    { key: 'dateCreated', label: 'Date Created', hidden: false },
    { key: 'country', label: 'Country', hidden: false },
    { key: 'type', label: 'Type', hidden: false },
    { key: 'localId', label: 'Local ID', hidden: false },
    { key: 'accountId', label: 'Account ID', hidden: false },
    { key: 'accountName', label: 'Account Name', hidden: false },
    { key: 'accountStatus', label: 'Account Status', hidden: false },
    { key: 'delivery', label: 'Delivery', hidden: false },
    { key: 'feedback', label: 'Feedback', hidden: false },
    { key: 'globalId', label: 'Global ID', hidden: true },
    { key: 'dateSent', label: 'Date Sent', hidden: true },
    { key: 'dateDelivered', label: 'Date Delivered', hidden: true },
    { key: 'dateRead', label: 'Date Read', hidden: true },
    { key: 'dateFeedback', label: 'Date Feedback', hidden: true },
    { key: 'deliveryFailures', label: 'Delivery Failures', hidden: true },
    { key: 'daysToOpen', label: 'Days to Open', hidden: true },
    { key: 'daysToSubmit', label: 'Days to Submit', hidden: true }
  ];

  // Display only visible columns
  get displayedColumns(): string[] {
    return this.allColumns.filter(col => !col.hidden).map(col => col.key);
  }

  constructor(
    public appUtils: AppUtils,
    public changeDetectorRef: ChangeDetectorRef,
    public qnsbnService: QnSbnService,
  ){}

  ngOnChanges(){
    if (this.dbTableData && Array.isArray(this.dbTableData)) {
      this.dataSource = [...this.dbTableData];
      this.filteredData = [...this.dataSource];
      this.totalCount = this.dataSource.length;
      this.updatePaginatedData();
    } 
    this.changeDetectorRef.detectChanges();
  }
  
  ngOnInit(): void {
    this.initializeTempColumnState();
    
    // Initialize data if available
    if (this.dbTableData && Array.isArray(this.dbTableData) && this.dbTableData.length > 0) {
      this.dataSource = [...this.dbTableData];
      this.filteredData = [...this.dataSource];
      this.totalCount = this.dataSource.length;
      this.updatePaginatedData();
    }
  }

  initializeTempColumnState(): void {
    this.allColumns.forEach(column => {
      this.tempColumnState[column.key] = !column.hidden;
    });
  }

  updatePaginatedData(): void {
    if (this.sortColumn) {
      this.applySort();
    }
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedData = this.filteredData.slice(startIndex, endIndex);
    this.paginatedData.forEach((item, index) => {
     
    });   
  }

  applySort(): void {
    if (!this.sortColumn) {
      return;
    }

    this.filteredData.sort((a, b) => {
      const aVal = a[this.sortColumn as keyof NotificationData];
      const bVal = b[this.sortColumn as keyof NotificationData];
      
      let comparison = 0;
      if (aVal! > bVal!) {
        comparison = 1;
      } else if (aVal! < bVal!) {
        comparison = -1;
      }
      
      return this.sortDirection === 'desc' ? comparison * -1 : comparison;
    });
  }

  onSort(column: string, direction: string): void {
    this.sortColumn = column;
    this.sortDirection = direction as 'asc' | 'desc';
    this.updatePaginatedData();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.performFilter(filterValue);
  }

  performFilter(filterValue: string): void {
    
    const searchStr = filterValue.trim().toLowerCase();
    
    if 
    (!searchStr) {
      this.filteredData = [...this.dataSource];
    } else {
      this.filteredData = this.dataSource.filter(data => 
        data.dateCreated?.toString().toLowerCase().includes(searchStr) ||
        data.country?.toLowerCase().includes(searchStr) ||
        data.type?.toLowerCase().includes(searchStr) ||
        data.localId?.toLowerCase().includes(searchStr) ||
        data.accountId?.toLowerCase().includes(searchStr) ||
        data.accountName?.toLowerCase().includes(searchStr) ||
        data.accountStatus?.toLowerCase().includes(searchStr) ||
        data.delivery?.toLowerCase().includes(searchStr) ||
        data.feedback?.toLowerCase().includes(searchStr) ||
        data.globalId?.toLowerCase().includes(searchStr)
      );
    }
    
    this.currentPage = 0;
    this.totalCount = this.filteredData.length;
    this.updatePaginatedData();
    this.changeDetectorRef.detectChanges();
  }

  clearSearch(): void {
    this.searchValue = '';
    this.performFilter('');
  }

  isColumnVisible(columnKey: string): boolean {
    const column = this.allColumns.find(col => col.key === columnKey);
    return column ? !column.hidden : false;
  }

  toggleColumn(columnKey: string): void {
    const column = this.allColumns.find(col => col.key === columnKey);
    if (column) {
      column.hidden = !column.hidden;
      this.changeDetectorRef.detectChanges();
    }
  }

  onColumnSelectionChange(columnKey: string, event: any): void {
    event.preventDefault();
    event.stopPropagation();
    // Toggle the current state
    this.tempColumnState[columnKey] = !this.getTempColumnState(columnKey);
  }

  getTempColumnState(columnKey: string): boolean {
    return this.tempColumnState[columnKey] !== undefined ? this.tempColumnState[columnKey] : !this.allColumns.find(col => col.key === columnKey)?.hidden;
  }

  areAllColumnsSelected(): boolean {
    return this.allColumns.every(col => this.getTempColumnState(col.key));
  }

  someColumnsSelected(): boolean {
    const selectedCount = this.allColumns.filter(col => this.getTempColumnState(col.key)).length;
    return selectedCount > 0 && selectedCount < this.allColumns.length;
  }

  toggleAllColumns(): void {
    const allSelected = this.areAllColumnsSelected();
    this.allColumns.forEach(column => {
      this.tempColumnState[column.key] = !allSelected;
    });
  }

  toggleColumnMenu(): void {
    this.showColumnMenu = !this.showColumnMenu;
    
    if (this.showColumnMenu) {
      // Re-initialize temp state when opening menu
      this.initializeTempColumnState();
      // Add click listener to close menu when clicking outside
      setTimeout(() => {
        document.addEventListener('click', this.onDocumentClick.bind(this), true);
      }, 0);
    } else {
      // Remove click listener when closing menu
      document.removeEventListener('click', this.onDocumentClick.bind(this), true);
    }
  }

  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const columnControls = target.closest('.column-controls');
    
    if (!columnControls && this.showColumnMenu) {
      this.showColumnMenu = false;
      document.removeEventListener('click', this.onDocumentClick.bind(this), true);
      this.changeDetectorRef.detectChanges();
    }
  }

  applyColumnChanges(): void {
    // Check if no columns would be visible
    const selectedColumns = this.allColumns.filter(col => this.getTempColumnState(col.key));
    
    if (selectedColumns.length === 0) {
      // If no columns are selected, revert to default columns
      this.allColumns.forEach(column => {
        column.hidden = this.defaultColumns.includes(column.key) ? false : true;
        this.tempColumnState[column.key] = this.defaultColumns.includes(column.key);
      });
    } else {
      // Apply the selected state normally
      this.allColumns.forEach(column => {
        column.hidden = !this.getTempColumnState(column.key);
      });
    }
    
    this.showColumnMenu = false;
    this.changeDetectorRef.detectChanges();
  }

  selectAllColumns(): void {
    this.allColumns.forEach(column => {
      column.hidden = false;
    });
    this.changeDetectorRef.detectChanges();
  }

  deselectAllColumns(): void {
    this.allColumns.forEach(column => {
      // Keep default columns visible
      if (this.defaultColumns.includes(column.key)) {
        column.hidden = false;
      } else {
        column.hidden = true;
      }
    });
    this.changeDetectorRef.detectChanges();
  }

  trackByColumn(index: number, column: ColumnDefinition): string {
    return column.key;
  }

  onPageChange(pageIndex: number, pageSize: number): void {
    this.currentPage = pageIndex;
    this.pageSize = pageSize;
    this.updatePaginatedData();
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get startIndex(): number {
    return this.currentPage * this.pageSize + 1;
  }

  get endIndex(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.totalCount);
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = parseInt(target.value);
    this.currentPage = 0;
    this.updatePaginatedData();
  }

  goToFirstPage(): void {
    this.currentPage = 0;
    this.updatePaginatedData();
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updatePaginatedData();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.updatePaginatedData();
    }
  }

  goToLastPage(): void {
    this.currentPage = this.totalPages - 1;
    this.updatePaginatedData();
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'send': return 'status-send';
      case 'sent': return 'status-sent';
      case 'delivered': return 'status-delivered';
      case 'delivery failed': return 'status-delivery-failed';
      case 'reminder': return 'status-reminder';
      case 'read': return 'status-read';
      case 'feedback overdue': return 'status-feedback-overdue';
      case 'feedback received': return 'status-feedback-received';
      case 'feedback submitted': return 'status-feedback-submitted';
      case 'failed': return 'status-failed';
      default: return 'status-default';
    }
  }

  getDeliveryClass(delivery: string): string {
    switch (delivery?.toLowerCase()) {
      case 'delivered': return 'delivery-success';
      case 'failure': return 'delivery-failure';
      default: return 'delivery-default';
    }
  }

  getFeedbackClass(feedback: string): string {
    switch (feedback?.toLowerCase()) {
      case 'submitted': return 'feedback-submitted';
      case 'pending': return 'feedback-pending';
      default: return 'feedback-default';
    }
  }

  // Legacy method for backward compatibility
  searchText(searchValue: string): void {
    this.performFilter(searchValue);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    document.removeEventListener('click', this.onDocumentClick.bind(this), true);
  }
}
